// Eventos del expediente — audiencias, vencimientos, reuniones, etc.
// El cron de backend manda notificaciones al apoderado y co-apoderados N días
// antes de cada evento (Recordar_Dias_Antes, default 1).

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Box, Typography, IconButton, TextField, Button, Chip, Tooltip, MenuItem,
    Paper, Dialog, DialogTitle, DialogContent, DialogActions,
    CircularProgress, Stack, Divider, Alert, Autocomplete,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { es } from 'date-fns/locale';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import GavelIcon from '@mui/icons-material/Gavel';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import GroupsIcon from '@mui/icons-material/Groups';
import EventIcon from '@mui/icons-material/Event';
import AlarmIcon from '@mui/icons-material/Alarm';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HistoryToggleOffIcon from '@mui/icons-material/HistoryToggleOff';
import EngineeringIcon from '@mui/icons-material/Engineering';
import {
    eventosListarJWT, eventosCrearJWT, eventosActualizarJWT, eventosEliminarJWT,
    eventosGetCompartidosJWT, eventosSetCompartidosJWT, personalBuscarRapidoJWT,
} from '../../api/endpoints';
import { usePermisos } from '../../utils/usePermisos';

const TIPOS = [
    { v: 'audiencia',   label: 'Audiencia',     icon: <GavelIcon fontSize="small" />,        color: '#6a1b9a' },
    { v: 'vencimiento', label: 'Vencimiento',   icon: <EventBusyIcon fontSize="small" />,    color: '#c62828' },
    { v: 'reunion',     label: 'Reunión',       icon: <GroupsIcon fontSize="small" />,       color: '#1565c0' },
    { v: 'inspeccion',  label: 'Inspección',    icon: <EngineeringIcon fontSize="small" />,  color: '#00796b' },
    { v: 'otro',        label: 'Otro',          icon: <EventIcon fontSize="small" />,        color: '#546e7a' },
];
const tipoInfo = (v) => TIPOS.find(t => t.v === v) || TIPOS[3];

// Presets para el recordatorio: cuánto antes del evento avisar a los apoderados.
// 'none' = sin recordatorio. 'custom' = el usuario elige fecha/hora exacta.
const PRESETS_RECORDATORIO = [
    { key: 'none',   label: 'Sin recordatorio', offsetMin: null },
    { key: '15min',  label: '15 minutos antes', offsetMin: 15 },
    { key: '1h',     label: '1 hora antes',     offsetMin: 60 },
    { key: '3h',     label: '3 horas antes',    offsetMin: 180 },
    { key: '1d',     label: '1 día antes',      offsetMin: 60 * 24 },
    { key: '3d',     label: '3 días antes',     offsetMin: 60 * 24 * 3 },
    { key: '1w',     label: '1 semana antes',   offsetMin: 60 * 24 * 7 },
    { key: 'custom', label: 'Personalizado…',   offsetMin: null },
];

function calcularRecordatorio(presetKey, fechaEvento, customFecha) {
    if (presetKey === 'none') return null;
    if (presetKey === 'custom') return customFecha || null;
    const preset = PRESETS_RECORDATORIO.find(p => p.key === presetKey);
    if (!preset || preset.offsetMin == null || !fechaEvento) return null;
    return new Date(new Date(fechaEvento).getTime() - preset.offsetMin * 60 * 1000);
}

function detectPresetFromRecordatorio(fechaEvento, fechaRecordatorio) {
    if (!fechaRecordatorio) return 'none';
    if (!fechaEvento) return 'custom';
    const diffMin = Math.round((new Date(fechaEvento).getTime() - new Date(fechaRecordatorio).getTime()) / 60000);
    const match = PRESETS_RECORDATORIO.find(p => p.offsetMin === diffMin);
    return match?.key || 'custom';
}

const EMPTY = { tipo: 'audiencia', titulo: '', descripcion: '', fechaEvento: null, recordatorioPreset: '1d', recordatorioCustom: null };

// Detecta si el usuario tiene permiso en el modulo global "SECRETARIA GENERAL Y DAP".
// Si tiene cualquier funcion ahi, lo consideramos admin de eventos y le mostramos
// la UI para compartir el recordatorio con personas adicionales.
function usuarioEsSecGral(permisos) {
    if (!permisos || typeof permisos !== 'object') return false;
    const ACCENT_RE = /[̀-ͯ]/g; // diacriticos combinatorios
    return Object.keys(permisos).some(k => {
        const norm = String(k).normalize('NFD').replace(ACCENT_RE, '').toUpperCase();
        return norm === 'SECRETARIA GENERAL Y DAP';
    });
}

function fmtFecha(d) {
    if (!d) return '';
    const date = new Date(d);
    // hour12:false fuerza 24h — toLocaleString('es-AR') default usa AM/PM en Chrome.
    return date.toLocaleString('es-AR', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit', hour12: false,
    });
}

function EventosTab({ idExpediente }) {
    const { permisos } = usePermisos();
    const puedeCompartir = useMemo(() => usuarioEsSecGral(permisos), [permisos]);

    const [eventos, setEventos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editData, setEditData] = useState(EMPTY);
    const [editId, setEditId] = useState(null);
    const [saving, setSaving] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(null);

    // Personas con quien se comparte la notificacion del recordatorio (#11).
    // Cada item: { Id_Personal, Nombre }
    const [compartidos, setCompartidos] = useState([]);
    const [personalOptions, setPersonalOptions] = useState([]);
    const [personalLoading, setPersonalLoading] = useState(false);
    const [busquedaPersonal, setBusquedaPersonal] = useState('');

    const cargar = useCallback(async () => {
        setLoading(true);
        try {
            const r = await eventosListarJWT(idExpediente);
            if (r?.ok) setEventos(r.items || []);
        } finally { setLoading(false); }
    }, [idExpediente]);

    useEffect(() => { cargar(); }, [cargar]);

    const { futuros, pasados } = useMemo(() => {
        const fut = [], pas = [];
        for (const e of eventos) (e.Pasado ? pas : fut).push(e);
        // Futuros: el más próximo primero. Pasados: el más reciente primero.
        fut.sort((a, b) => new Date(a.Fecha_Evento) - new Date(b.Fecha_Evento));
        pas.sort((a, b) => new Date(b.Fecha_Evento) - new Date(a.Fecha_Evento));
        return { futuros: fut, pasados: pas };
    }, [eventos]);

    const abrirNuevo = () => {
        setEditId(null);
        setEditData({ ...EMPTY, fechaEvento: null });
        setCompartidos([]);
        setDialogOpen(true);
    };

    const abrirEditar = async (ev) => {
        setEditId(ev.Id);
        const fechaEv = ev.Fecha_Evento ? new Date(ev.Fecha_Evento) : null;
        const fechaRec = ev.Fecha_Recordatorio ? new Date(ev.Fecha_Recordatorio) : null;
        const presetDetectado = detectPresetFromRecordatorio(fechaEv, fechaRec);
        setEditData({
            tipo: ev.Tipo,
            titulo: ev.Titulo || '',
            descripcion: ev.Descripcion || '',
            fechaEvento: fechaEv,
            recordatorioPreset: presetDetectado,
            recordatorioCustom: presetDetectado === 'custom' ? fechaRec : null,
        });
        setCompartidos([]);
        setDialogOpen(true);
        if (puedeCompartir) {
            try {
                const r = await eventosGetCompartidosJWT(ev.Id);
                if (r?.ok) setCompartidos(r.items || []);
            } catch (e) { /* silencioso, el dialog igual abre */ }
        }
    };

    // Busqueda de personas para el autocomplete (debounce minimo via useEffect).
    useEffect(() => {
        if (!puedeCompartir || !dialogOpen) return;
        const q = (busquedaPersonal || '').trim();
        if (q.length < 2) { setPersonalOptions([]); return; }
        let cancelado = false;
        setPersonalLoading(true);
        const tid = setTimeout(async () => {
            try {
                const r = await personalBuscarRapidoJWT(q, 12);
                if (!cancelado && r?.ok) {
                    setPersonalOptions(r.items || r.personal || []);
                }
            } catch { /* ignore */ }
            finally { if (!cancelado) setPersonalLoading(false); }
        }, 250);
        return () => { cancelado = true; clearTimeout(tid); };
    }, [busquedaPersonal, puedeCompartir, dialogOpen]);

    const guardar = async () => {
        if (!editData.titulo.trim() || !editData.fechaEvento) return;
        setSaving(true);
        try {
            const fechaRec = calcularRecordatorio(editData.recordatorioPreset, editData.fechaEvento, editData.recordatorioCustom);
            const payload = {
                idExpediente,
                tipo: editData.tipo,
                titulo: editData.titulo.trim(),
                descripcion: editData.descripcion || null,
                fechaEvento: editData.fechaEvento instanceof Date ? editData.fechaEvento.toISOString() : editData.fechaEvento,
                fechaRecordatorio: fechaRec ? (fechaRec instanceof Date ? fechaRec.toISOString() : fechaRec) : null,
            };
            let idEventoFinal = editId;
            if (editId) {
                await eventosActualizarJWT(editId, payload);
            } else {
                const r = await eventosCrearJWT(payload);
                idEventoFinal = r?.id || null;
            }
            // Guardar lista de compartidos si el usuario es Sec Gral/DAP. Si no
            // tiene permiso, el backend no recibe payload y los actuales quedan
            // intactos (en alta sin compartidos no llamamos).
            if (puedeCompartir && idEventoFinal) {
                const ids = compartidos.map(c => Number(c.Id_Personal)).filter(n => Number.isFinite(n) && n > 0);
                try {
                    await eventosSetCompartidosJWT(idEventoFinal, ids);
                } catch (e) { /* no bloquear el guardado por esto */ }
            }
            setDialogOpen(false);
            await cargar();
        } finally { setSaving(false); }
    };

    const eliminar = async (id) => {
        await eventosEliminarJWT(id);
        setConfirmDelete(null);
        await cargar();
    };

    const renderEvento = (ev) => {
        const info = tipoInfo(ev.Tipo);
        const dias = ev.Dias_Hasta;
        // "Inminente" = ya entró en la ventana de recordatorio (Fecha_Recordatorio <= ahora)
        const inminente = !ev.Pasado && ev.Fecha_Recordatorio
            && new Date(ev.Fecha_Recordatorio).getTime() <= Date.now();
        const fechaRecTxt = ev.Fecha_Recordatorio ? fmtFecha(ev.Fecha_Recordatorio) : null;
        return (
            <Paper key={ev.Id} variant="outlined" sx={{
                p: 1.5, mb: 1, borderRadius: '4px',
                borderLeft: `4px solid ${info.color}`,
                opacity: ev.Pasado ? 0.6 : 1,
                bgcolor: inminente ? '#fff8e1' : 'white',
            }}>
                <Stack direction="row" alignItems="flex-start" spacing={1.5}>
                    <Box sx={{ color: info.color, mt: 0.3 }}>{info.icon}</Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.3 }}>
                            <Chip size="small" label={info.label}
                                sx={{ bgcolor: info.color + '22', color: info.color, fontWeight: 600, height: 20 }} />
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>{ev.Titulo}</Typography>
                            {ev.Notificado === 1 && !ev.Pasado && (
                                <Tooltip title="Recordatorio enviado"><CheckCircleIcon sx={{ fontSize: 14, color: '#2e7d32' }} /></Tooltip>
                            )}
                            {ev.Pasado && (
                                <Tooltip title="Evento pasado"><HistoryToggleOffIcon sx={{ fontSize: 14, color: '#888' }} /></Tooltip>
                            )}
                        </Stack>
                        <Typography variant="caption" sx={{ display: 'block', color: '#555', mb: 0.3 }}>
                            📅 {fmtFecha(ev.Fecha_Evento)}
                            {!ev.Pasado && dias != null && (
                                <span style={{ marginLeft: 8, fontWeight: 600, color: inminente ? '#e65100' : '#1976d2' }}>
                                    {dias === 0 ? '· HOY' : dias === 1 ? '· mañana' : `· en ${dias} días`}
                                </span>
                            )}
                            <span style={{ marginLeft: 8, color: '#999' }}>
                                <AlarmIcon sx={{ fontSize: 11, verticalAlign: 'middle', mr: 0.3 }} />
                                {fechaRecTxt ? `avisa el ${fechaRecTxt}` : 'sin recordatorio'}
                            </span>
                        </Typography>
                        {ev.Descripcion && (
                            <Typography variant="body2" sx={{ color: '#444', whiteSpace: 'pre-wrap', fontSize: '0.85rem' }}>
                                {ev.Descripcion}
                            </Typography>
                        )}
                    </Box>
                    <Stack direction="row">
                        <Tooltip title="Editar"><IconButton size="small" onClick={() => abrirEditar(ev)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Eliminar"><IconButton size="small" color="error" onClick={() => setConfirmDelete(ev)}><DeleteOutlineIcon fontSize="small" /></IconButton></Tooltip>
                    </Stack>
                </Stack>
            </Paper>
        );
    };

    return (
        <Box sx={{ p: 1 }}>
            <Stack direction="row" alignItems="center" sx={{ mb: 1.5 }}>
                <Typography variant="h6" sx={{ flex: 1, fontWeight: 600 }}>
                    Eventos del expediente
                </Typography>
                <Button variant="contained" size="small" startIcon={<AddIcon />}
                    onClick={abrirNuevo}
                    sx={{ borderRadius: '4px', textTransform: 'none', fontWeight: 600 }}>
                    Nuevo evento
                </Button>
            </Stack>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>
            ) : eventos.length === 0 ? (
                <Alert severity="info" sx={{ borderRadius: '4px' }}>
                    No hay eventos registrados. Agregá audiencias, vencimientos de plazo o reuniones para que el sistema te avise antes de cada uno.
                </Alert>
            ) : (
                <>
                    {futuros.length > 0 && (
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="caption" sx={{ fontWeight: 700, color: '#666', textTransform: 'uppercase', letterSpacing: 1 }}>
                                Próximos ({futuros.length})
                            </Typography>
                            <Box sx={{ mt: 0.5 }}>{futuros.map(renderEvento)}</Box>
                        </Box>
                    )}
                    {pasados.length > 0 && (
                        <Box>
                            <Divider sx={{ my: 1.5 }} />
                            <Typography variant="caption" sx={{ fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: 1 }}>
                                Pasados ({pasados.length})
                            </Typography>
                            <Box sx={{ mt: 0.5 }}>{pasados.map(renderEvento)}</Box>
                        </Box>
                    )}
                </>
            )}

            {/* Dialog crear/editar */}
            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth
                PaperProps={{ sx: { borderRadius: '4px' } }}>
                <DialogTitle sx={{ fontWeight: 700, color: '#1769aa' }}>
                    {editId ? 'Editar evento' : 'Nuevo evento'}
                </DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2} sx={{ pt: 1 }}>
                        <TextField select size="small" label="Tipo" value={editData.tipo}
                            onChange={e => setEditData(d => ({ ...d, tipo: e.target.value }))}>
                            {TIPOS.map(t => (
                                <MenuItem key={t.v} value={t.v}>
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <Box sx={{ color: t.color }}>{t.icon}</Box>
                                        <span>{t.label}</span>
                                    </Stack>
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField size="small" label="Título" value={editData.titulo}
                            onChange={e => setEditData(d => ({ ...d, titulo: e.target.value }))}
                            // Chrome autocompleta lo tipeado previamente en este "name" — útil porque
                            // los abogados crean muchos eventos con títulos similares (ej "Audiencia preliminar").
                            name="sigif-evento-titulo"
                            inputProps={{ autoComplete: 'on' }}
                            required autoFocus />
                        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
                            <DateTimePicker
                                label="Fecha y hora del evento *"
                                value={editData.fechaEvento}
                                onChange={(val) => setEditData(d => ({ ...d, fechaEvento: val }))}
                                ampm={false} format="dd/MM/yyyy HH:mm"
                                slotProps={{ textField: { size: 'small', fullWidth: true } }}
                            />
                        </LocalizationProvider>
                        <TextField select size="small" label="Recordarme"
                            value={editData.recordatorioPreset}
                            onChange={e => setEditData(d => ({ ...d, recordatorioPreset: e.target.value }))}
                            helperText="Cuándo el sistema te avisa a vos y a los co-apoderados."
                        >
                            {PRESETS_RECORDATORIO.map(p => (
                                <MenuItem key={p.key} value={p.key}>{p.label}</MenuItem>
                            ))}
                        </TextField>
                        {editData.recordatorioPreset === 'custom' && (
                            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
                                <DateTimePicker
                                    label="Fecha y hora del recordatorio *"
                                    value={editData.recordatorioCustom}
                                    onChange={(val) => setEditData(d => ({ ...d, recordatorioCustom: val }))}
                                    ampm={false} format="dd/MM/yyyy HH:mm"
                                    slotProps={{ textField: { size: 'small', fullWidth: true,
                                        helperText: 'Debe ser anterior al evento.' } }}
                                />
                            </LocalizationProvider>
                        )}
                        <TextField size="small" label="Descripción (opcional)" value={editData.descripcion}
                            multiline minRows={3} maxRows={8}
                            onChange={e => setEditData(d => ({ ...d, descripcion: e.target.value }))} />

                        {/* Compartir recordatorio con personas adicionales (#11).
                            Solo visible para usuarios con permiso "Secretaría General y DAP". */}
                        {puedeCompartir && (
                            <Autocomplete
                                multiple
                                size="small"
                                value={compartidos}
                                options={personalOptions.map(p => ({
                                    Id_Personal: Number(p.Id ?? p.Id_Personal ?? p.id ?? 0),
                                    Nombre: (`${p.Apellido || p.apellido || ''}, ${p.Nombre || p.nombre || ''}`).replace(/^, |, $/g, '').trim()
                                        || (p.Apellido_Nombre || p.label || `#${p.Id || p.Id_Personal || ''}`),
                                })).filter(o => o.Id_Personal > 0)}
                                getOptionLabel={(opt) => opt?.Nombre || ''}
                                isOptionEqualToValue={(a, b) => a.Id_Personal === b.Id_Personal}
                                filterOptions={(x) => x}
                                loading={personalLoading}
                                onInputChange={(_, v) => setBusquedaPersonal(v)}
                                onChange={(_, val) => setCompartidos(val || [])}
                                renderInput={(params) => (
                                    <TextField {...params}
                                        label="Compartir recordatorio con (opcional)"
                                        placeholder="Buscar personal..."
                                        helperText="Estas personas también recibirán la notificación del recordatorio. Solo visible para Sec Gral / DAP."
                                    />
                                )}
                                renderTags={(value, getTagProps) =>
                                    value.map((opt, i) => (
                                        <Chip {...getTagProps({ index: i })} key={opt.Id_Personal} label={opt.Nombre} size="small" />
                                    ))
                                }
                            />
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)}>Cancelar</Button>
                    <Button onClick={guardar} variant="contained"
                        disabled={!editData.titulo.trim() || !editData.fechaEvento || saving}
                        sx={{ textTransform: 'none', fontWeight: 600 }}>
                        {saving ? 'Guardando…' : 'Guardar'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Confirmar eliminación */}
            <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)} maxWidth="xs" fullWidth
                PaperProps={{ sx: { borderRadius: '4px' } }}>
                <DialogTitle>¿Eliminar evento?</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        Vas a eliminar <strong>{confirmDelete?.Titulo}</strong>. Esta acción no se puede deshacer.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDelete(null)}>Cancelar</Button>
                    <Button color="error" variant="contained" onClick={() => eliminar(confirmDelete.Id)}>Eliminar</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default EventosTab;
